import React from 'react';
import { useSelector } from 'react-redux';

import { selectUser } from '../containers/App/selectors';
import {
  getDesignVersionLocalStorage,
  setDesignVersionLocalStorage,
  type DesignVersion,
} from '../localStorage/designVersionLocalStorage';
import {
  DesignVersionContext,
  type DesignVersionContextValue,
} from './designVersionContext.shared';

export function DesignVersionProvider({ children }: { children: React.ReactNode }) {
  const user = useSelector(selectUser);
  const [designVersion, setDesignVersionState] = React.useState<DesignVersion>(() =>
    getDesignVersionLocalStorage(),
  );

  const canUseModernDesign = Boolean(user?.isAdmin);
  const isModernDesign = canUseModernDesign && designVersion === 'modern';

  React.useLayoutEffect(() => {
    // The document boundary also covers dropdowns and modals portaled to body.
    document.documentElement.dataset.design = isModernDesign ? 'modern' : 'legacy';
    document.body.classList.toggle('v2-theme', isModernDesign);

    return () => {
      document.documentElement.dataset.design = 'legacy';
      document.body.classList.remove('v2-theme');
    };
  }, [isModernDesign]);

  const setDesignVersion = React.useCallback(
    (version: DesignVersion) => {
      if (version === 'modern' && !canUseModernDesign) {
        return;
      }

      setDesignVersionLocalStorage(version);
      setDesignVersionState(version);
    },
    [canUseModernDesign],
  );

  const toggleDesignVersion = React.useCallback(() => {
    setDesignVersion(isModernDesign ? 'legacy' : 'modern');
  }, [isModernDesign, setDesignVersion]);

  const value = React.useMemo<DesignVersionContextValue>(
    () => ({
      isModernDesign,
      canUseModernDesign,
      designVersion: isModernDesign ? ('modern' as const) : ('legacy' as const),
      setDesignVersion,
      toggleDesignVersion,
    }),
    [canUseModernDesign, isModernDesign, setDesignVersion, toggleDesignVersion],
  );

  return <DesignVersionContext.Provider value={value}>{children}</DesignVersionContext.Provider>;
}
